/**
 * Extract dominant color from image URL using Canvas API
 * @param {string} imageUrl - URL of the image
 * @returns {Promise<{color: string, isDark: boolean}>}
 */
export const extractDominantColor = async (imageUrl) => {
  try {
    if (!imageUrl) {
      return { color: '#f5f5f5', isDark: false };
    }

    // Create a new image element to ensure loading
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    // Wait for image to load
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = imageUrl;
    });

    // Create canvas to extract pixel data
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size (sample smaller area for performance)
    const sampleSize = 50;
    canvas.width = sampleSize;
    canvas.height = sampleSize;
    
    // Draw image scaled down
    ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
    
    // Get pixel data
    const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
    const pixels = imageData.data;
    
    // Color frequency map
    const colorMap = {};
    let maxCount = 0;
    let dominantColor = null;
    
    // Sample pixels (skip every 4th pixel for performance)
    for (let i = 0; i < pixels.length; i += 16) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      const a = pixels[i + 3];
      
      // Skip transparent pixels
      if (a < 128) continue;
      
      // Round colors to reduce variations
      const roundedR = Math.round(r / 20) * 20;
      const roundedG = Math.round(g / 20) * 20;
      const roundedB = Math.round(b / 20) * 20;
      
      const key = `${roundedR},${roundedG},${roundedB}`;
      
      colorMap[key] = (colorMap[key] || 0) + 1;
      
      if (colorMap[key] > maxCount) {
        maxCount = colorMap[key];
        dominantColor = { r: roundedR, g: roundedG, b: roundedB };
      }
    }
    
    // If no dominant color found, use average
    if (!dominantColor) {
      let totalR = 0, totalG = 0, totalB = 0, count = 0;
      for (let i = 0; i < pixels.length; i += 4) {
        if (pixels[i + 3] < 128) continue;
        totalR += pixels[i];
        totalG += pixels[i + 1];
        totalB += pixels[i + 2];
        count++;
      }
      if (count > 0) {
        dominantColor = {
          r: Math.round(totalR / count),
          g: Math.round(totalG / count),
          b: Math.round(totalB / count)
        };
      } else {
        dominantColor = { r: 245, g: 245, b: 245 };
      }
    }
    
    // Validate RGB values and clamp to valid range
    const clampRGB = (value) => Math.max(0, Math.min(255, Math.round(value)));
    const validR = clampRGB(dominantColor.r);
    const validG = clampRGB(dominantColor.g);
    const validB = clampRGB(dominantColor.b);
    
    // Convert to hex with validation
    const toHex = (n) => {
      const clamped = clampRGB(n);
      const hex = clamped.toString(16).padStart(2, '0');
      return hex;
    };
    const hex = `#${toHex(validR)}${toHex(validG)}${toHex(validB)}`;
    
    // Validate hex format
    const hexPattern = /^#([A-Fa-f0-9]{6})$/;
    if (!hexPattern.test(hex)) {
      console.warn('Invalid hex color generated, using fallback:', hex);
      return { color: '#f5f5f5', isDark: false };
    }
    
    // Calculate luminance for contrast detection
    const luminance = calculateLuminance(validR, validG, validB);
    const isDark = luminance < 0.5;
    
    return {
      color: hex,
      isDark: isDark,
      rgb: [validR, validG, validB],
      luminance: luminance
    };
  } catch (error) {
    console.error('Error extracting color:', error);
    // Return default color on error
    return { color: '#f5f5f5', isDark: false };
  }
};

/**
 * Calculate relative luminance of RGB color
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {number} Luminance value (0-1)
 */
export const calculateLuminance = (r, g, b) => {
  // Normalize RGB values
  const normalize = (val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  };

  const rNorm = normalize(r);
  const gNorm = normalize(g);
  const bNorm = normalize(b);

  // Calculate relative luminance
  return 0.2126 * rNorm + 0.7152 * gNorm + 0.0722 * bNorm;
};

/**
 * Get contrasting text color based on background
 * @param {boolean} isDark - Whether the background is dark
 * @returns {object} Text colors for primary and secondary text
 */
export const getContrastingTextColors = (isDark) => {
  if (isDark) {
    return {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      button: '#ffffff',
      buttonBg: 'rgba(255, 255, 255, 0.1)',
      buttonHover: 'rgba(255, 255, 255, 0.2)'
    };
  } else {
    return {
      primary: '#000000',
      secondary: 'rgba(0, 0, 0, 0.6)',
      button: '#000000',
      buttonBg: 'rgba(0, 0, 0, 0.05)',
      buttonHover: 'rgba(0, 0, 0, 0.1)'
    };
  }
};

/**
 * Create a subtle gradient background from base color
 * @param {string} baseColor - Hex color
 * @param {boolean} isDark - Whether the color is dark
 * @returns {string} CSS gradient string
 */
export const createGradientBackground = (baseColor, isDark) => {
  try {
    // Validate hex color format
    const hexPattern = /^#([A-Fa-f0-9]{6})$/;
    if (!hexPattern.test(baseColor)) {
      console.warn('Invalid base color for gradient, using fallback:', baseColor);
      return '#f5f5f5';
    }
    
    // Create subtle gradient for more visual interest
    const opacity = isDark ? 0.95 : 0.9;
    const opacityHex = Math.round(opacity * 255).toString(16).padStart(2, '0');
    
    // Ensure opacity hex is valid
    if (opacityHex.length !== 2 || !/^[A-Fa-f0-9]{2}$/.test(opacityHex)) {
      console.warn('Invalid opacity hex generated, using solid color');
      return baseColor;
    }
    
    return `linear-gradient(135deg, ${baseColor} 0%, ${baseColor}${opacityHex} 100%)`;
  } catch (error) {
    console.error('Error creating gradient:', error);
    return '#f5f5f5';
  }
};